import React, { useEffect, useRef, useState } from "react";
import { Search, X, UserPlus } from "lucide-react";
import { staffApi } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";

// Searchable customer picker.
//
// Two visual modes:
//   • Empty (value=null): a search input; typing (>=1 char) fires a debounced
//     `/customers?search=` query and renders matches below. Clicking a row
//     calls `onPick(customer)` and clears the input.
//   • Picked (value=object): a chip showing the picked customer's name + a
//     small ✕ button that calls `onClear()` to reset back to search mode.
//
// Props:
//   value      → the currently-picked customer object, or null
//   onPick     → callback(customer) when a row is clicked
//   onClear    → callback() when the ✕ chip is clicked
//   allowCreate→ when true, no exact match, and query is non-empty, shows a
//                "Use as new customer" row that emits `onPick({ __new: true,
//                full_name: query })` so callers can branch on the sentinel.
//   placeholder→ input placeholder (default "Search by name, email, phone…")
//   excludeId  → customer id to exclude from results (used by "source" picker)
//   testId     → data-testid prefix (default "customer-combobox")
export default function CustomerCombobox({
  value,
  onPick,
  onClear,
  allowCreate = false,
  placeholder = "Search by name, email, phone…",
  excludeId = null,
  testId = "customer-combobox",
}) {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const boxRef = useRef(null);

  // Debounced server search. Empty query → clear + close.
  useEffect(() => {
    if (value) return;
    const q = query.trim();
    if (!q) { setResults([]); setOpen(false); return; }
    setLoading(true);
    const t = setTimeout(async () => {
      try {
        const r = await staffApi.get("/customers", { params: { search: q } });
        const rows = (r.data || []).filter((c) => !excludeId || c.id !== excludeId).slice(0, 8);
        setResults(rows);
        setOpen(true);
      } catch { setResults([]); }
      finally { setLoading(false); }
    }, 220);
    return () => clearTimeout(t);
  }, [query, value, excludeId]);

  // Close the dropdown when the operator clicks outside the picker.
  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false);
    };
    window.addEventListener("mousedown", handler);
    return () => window.removeEventListener("mousedown", handler);
  }, [open]);

  if (value) {
    return (
      <div
        className="flex items-center gap-2 rounded-md border border-border bg-muted/30 px-3 py-1.5 text-sm"
        data-testid={`${testId}-chip`}
      >
        <div className="flex-1 min-w-0">
          <span className="font-medium truncate block">
            {value.full_name || `${value.first_name || ""} ${value.last_name || ""}`.trim() || value.email || "Customer"}
          </span>
          <span className="text-xs text-muted-foreground truncate block">
            {value.email || ""}{value.email && value.whatsapp ? " · " : ""}{value.whatsapp || ""}
          </span>
        </div>
        <button
          type="button"
          onClick={onClear}
          className="p-1 rounded hover:bg-secondary text-muted-foreground hover:text-foreground"
          aria-label="Clear customer"
          data-testid={`${testId}-clear`}
        >
          <X className="w-3.5 h-3.5" />
        </button>
      </div>
    );
  }

  const exactMatch = results.some(
    (r) => (r.full_name || "").toLowerCase() === query.trim().toLowerCase(),
  );

  return (
    <div ref={boxRef} className="relative" data-testid={testId}>
      <div className="relative">
        <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" />
        <Input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onFocus={() => { if (results.length) setOpen(true); }}
          placeholder={placeholder}
          className="pl-9"
          data-testid={`${testId}-input`}
        />
      </div>
      {open && (query.trim().length > 0) && (
        <div className="absolute z-30 top-full left-0 right-0 mt-1 rounded-md border border-border bg-white shadow-lg max-h-64 overflow-auto">
          {loading && (
            <div className="px-3 py-2 text-xs text-muted-foreground">Searching…</div>
          )}
          {!loading && results.length === 0 && !allowCreate && (
            <div className="px-3 py-2 text-xs text-muted-foreground">No customers match &ldquo;{query}&rdquo;.</div>
          )}
          {results.map((c) => (
            <button
              key={c.id}
              type="button"
              onClick={() => { onPick(c); setQuery(""); setOpen(false); }}
              className="w-full text-left px-3 py-2 text-sm hover:bg-secondary/60 border-b border-border/50 last:border-0"
              data-testid={`${testId}-item-${c.id}`}
            >
              <div className="font-medium truncate">
                {c.full_name || `${c.first_name || ""} ${c.last_name || ""}`.trim() || "—"}
              </div>
              <div className="text-xs text-muted-foreground truncate">
                {c.email || ""}{c.email && c.whatsapp ? " · " : ""}{c.whatsapp || ""}
              </div>
            </button>
          ))}
          {allowCreate && !exactMatch && query.trim().length > 1 && (
            <button
              type="button"
              onClick={() => {
                onPick({ __new: true, full_name: query.trim() });
                setQuery("");
                setOpen(false);
              }}
              className={cn(
                "w-full text-left px-3 py-2 text-sm hover:bg-emerald-50 border-t border-border/50 flex items-center gap-2",
                results.length === 0 && "border-t-0",
              )}
              data-testid={`${testId}-create`}
            >
              <UserPlus className="w-3.5 h-3.5 text-emerald-700" />
              <span>Create new customer: <span className="font-medium">{query.trim()}</span></span>
            </button>
          )}
        </div>
      )}
    </div>
  );
}
